/**
 * Pricing page — canonical Plan Studio catalog (2026-07-11).
 *
 * Presentation contract (2026-08-01):
 *   • NO internal identifiers (version_number, internal_code, plan_family_id,
 *     plan_version_id, pricing_offer_id) are ever rendered on this public
 *     screen. Only commercial names + marketing badges are shown.
 *   • A single canonical `selectedOfferId` drives:
 *       - the highlighted plan card,
 *       - the sticky billing summary (recurring, setup, VAT, total),
 *       - the primary "Continue" CTA which forwards the canonical IDs
 *         to /register via query string.
 *   • Default selection is deterministic: first offer in backend catalog
 *     order for the active bundle+cycle. Marketing badges are display-only
 *     and NEVER force selection.
 *   • Switching bundle/cycle preserves the currently selected plan_family
 *     if an equivalent offer exists, else re-selects the first offer.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePlatformBranding, brandUrl } from "@/context/PlatformBrandingContext";
import { api } from "@/lib/api";
import {
  CheckCircle2, ShieldCheck, Globe2, Sparkles, Activity,
  FileText, FolderKanban, Wallet, RefreshCcw,
  UserCog, CreditCard as CardIcon, CalendarDays, ClipboardCheck, FileBadge2,
  ArrowRight, Lock,
} from "lucide-react";

const BUNDLES = [
  {
    key: "certification",
    label: "Certification ERP",
    blurb: "ISO certification operations end-to-end",
    bullets: [
      { icon: FolderKanban, text: "Cases · Submissions · Final Outputs" },
      { icon: FileText,     text: "Documents · Workflows · Renewals" },
      { icon: Wallet,       text: "Quotations · Invoicing · Receipts" },
      { icon: Activity,     text: "Pipeline · Leads · Reports" },
    ],
  },
  {
    key: "hr",
    label: "HR Payroll",
    blurb: "Employees, payroll, and time off",
    bullets: [
      { icon: UserCog,       text: "Employees · Roles · Documents" },
      { icon: CardIcon,      text: "Payroll · Payslips · WPS-ready" },
      { icon: CalendarDays,  text: "Leave · Attendance · Reimbursements" },
      { icon: ClipboardCheck,text: "My HR self-service portal" },
    ],
  },
  {
    key: "certification_hr",
    label: "Certification + HR",
    blurb: "Everything · Best value · Workspace switcher",
    popular: true,
    bullets: [
      { icon: FileBadge2,    text: "Full Certification ERP" },
      { icon: UserCog,       text: "Full HR & Payroll" },
      { icon: Sparkles,      text: "Workspace switcher" },
      { icon: ShieldCheck,   text: "All shared admin features" },
    ],
  },
];

const FAQ = [
  { q: "Can I upgrade later?", a: "Yes. Upgrades are pro-rated to the current billing cycle and take effect immediately." },
  { q: "Can I switch modules later?", a: "Yes. You can add or remove the HR module from your subscription any time. Existing tenant data is preserved." },
  { q: "How does billing work?", a: "Stripe-backed monthly or annual subscriptions. Setup fees (if any) are billed once on signup; subscription auto-renews until you cancel." },
  { q: "Do prices include tax?", a: "Tax is shown separately and added to the total at checkout. Invoices itemize subscription, setup, and tax." },
  { q: "How is setup charged?", a: "Setup is a one-time fee on the first invoice (where applicable). It covers onboarding, data migration assistance, and workspace provisioning." },
  { q: "Can I migrate data in / out later?", a: "Yes. CSV export is available throughout your subscription, and our team assists with migrations on higher-tier plans." },
];

export default function Pricing() {
  const nav = useNavigate();
  const [country, setCountry]           = useState(null);
  const [plans, setPlans]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [bundle, setBundle]             = useState("certification");
  const [cycle,  setCycle]              = useState("annual");
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [faqOpen, setFaqOpen]           = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/plan-catalog");
        const d = data?.data || {};
        setCountry(d.country || null);
        setPlans(Array.isArray(d.plans) ? d.plans : []);
      } finally { setLoading(false); }
    })();
  }, []);

  // Filter to current bundle + cycle. Backend already orders by
  // (catalog_order, name, cycle) so activePlans is deterministic.
  const activePlans = useMemo(() =>
    plans.filter(p => p.module_bundle === bundle && p.billing_cycle === cycle),
    [plans, bundle, cycle]
  );

  // Deterministic default / switch-preservation:
  //   1. keep the current selection if it still applies to this bundle+cycle,
  //   2. else try to preserve the same plan_family (equivalent offer at the
  //      new cycle),
  //   3. else fall back to the first offer in backend catalog order.
  // Marketing badges (Most Popular / Best Value) are NEVER used to auto-select.
  useEffect(() => {
    if (activePlans.length === 0) {
      if (selectedOfferId) setSelectedOfferId(null);
      return;
    }
    const current = activePlans.find(p => p.pricing_offer_id === selectedOfferId);
    if (current) return;

    // Try to preserve family across bundle/cycle switches.
    const prev = plans.find(p => p.pricing_offer_id === selectedOfferId);
    if (prev) {
      const eq = activePlans.find(p => p.plan_family_id === prev.plan_family_id);
      if (eq) { setSelectedOfferId(eq.pricing_offer_id); return; }
    }
    setSelectedOfferId(activePlans[0].pricing_offer_id);
  }, [bundle, cycle, activePlans]);

  const selectedOffer = useMemo(
    () => activePlans.find(p => p.pricing_offer_id === selectedOfferId) || null,
    [activePlans, selectedOfferId]
  );

  const fmt = (n) => {
    if (n === null || n === undefined) return "—";
    return `${country?.currency_code || "AED"} ${Number(n).toLocaleString()}`;
  };

  // Annual savings computed from the cheapest published plan pair.
  const annualSavingsPct = useMemo(() => {
    if (!plans.length) return null;
    const monthly = plans.filter(p => p.module_bundle === bundle && p.billing_cycle === "monthly");
    const annual  = plans.filter(p => p.module_bundle === bundle && p.billing_cycle === "annual");
    if (!monthly.length || !annual.length) return null;
    for (const m of monthly) {
      const a = annual.find(x => x.plan_version_id === m.plan_version_id);
      if (a && m.recurring_amount && a.recurring_amount) {
        const save = Math.round(((m.recurring_amount * 12 - a.recurring_amount) / (m.recurring_amount * 12)) * 100);
        if (save > 0) return save;
      }
    }
    return null;
  }, [plans, bundle]);

  const onContinue = () => {
    if (!selectedOffer) return;
    if (selectedOffer.contact_sales) {
      window.location.href = "mailto:sales@certopz.io?subject=CertOpz%20Enquiry";
      return;
    }
    const qs = new URLSearchParams({
      plan_version_id:  selectedOffer.plan_version_id,
      pricing_offer_id: selectedOffer.pricing_offer_id,
      module_bundle:    selectedOffer.module_bundle,
      billing_cycle:    selectedOffer.billing_cycle,
    }).toString();
    nav(`/register?${qs}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="pricing-page">
      <PricingHeader />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10 text-center">
        <span className="inline-block text-[10px] tracking-[0.3em] text-teal-700 font-bold uppercase mb-3">CertOpz · Subscription Plans</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
          Choose the right CertOpz subscription<br className="hidden sm:block" /> for your business
        </h1>
        <p className="mt-5 text-base text-slate-600 max-w-2xl mx-auto">
          Simple pricing. Enterprise capabilities. Built for certification and HR operations.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-teal-600" /> Transparent Pricing</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-teal-600" /> Tax Itemized</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-teal-600" /> Cancel Anytime</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-teal-600" /> Secure Subscription</span>
        </div>
      </section>

      {/* Country */}
      <section className="max-w-6xl mx-auto px-6 mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 shadow-sm px-3.5 py-1.5 text-sm" data-testid="pricing-country">
          <Globe2 className="w-4 h-4 text-teal-600" />
          <span className="text-slate-700">Country</span>
          <span className="text-slate-300">·</span>
          <span className="font-semibold text-slate-900">{country?.country_name || "—"}</span>
          <span className="text-slate-300">·</span>
          <span className="text-xs text-slate-500">{country?.currency_code || ""} · {country?.tax_name || "Tax"} {country?.tax_rate ?? 0}%</span>
        </div>
      </section>

      {/* Module selector */}
      <section className="max-w-6xl mx-auto px-6 mb-6">
        <div className="text-center text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-3">Choose your product</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pricing-bundle-group">
          {BUNDLES.map(b => {
            const active = bundle === b.key;
            return (
              <button
                key={b.key}
                onClick={() => setBundle(b.key)}
                className={`text-left rounded-2xl border p-5 transition-all duration-200 ${active ? "border-teal-500 bg-white shadow-md ring-2 ring-teal-500/20 -translate-y-0.5" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"}`}
                data-testid={`pricing-bundle-${b.key}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-4 h-4 rounded-full ${active ? "bg-teal-500 ring-4 ring-teal-100" : "border border-slate-300"}`} />
                  <span className="font-semibold text-slate-900">{b.label}</span>
                  {b.popular ? <span className="ml-auto text-[9px] uppercase tracking-widest font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Best value</span> : null}
                </div>
                <p className="text-xs text-slate-500 mb-3 ml-6">{b.blurb}</p>
                <ul className="space-y-1.5 ml-6">
                  {b.bullets.map((bl, i) => (
                    <li key={i} className="flex items-center gap-2 text-[12px] text-slate-700">
                      <bl.icon className="w-3.5 h-3.5 text-teal-500 shrink-0" />
                      <span>{bl.text}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      </section>

      {/* Billing cycle */}
      <section className="max-w-6xl mx-auto px-6 mb-8">
        <div className="flex flex-col items-center gap-3">
          <div className="inline-flex rounded-full border border-slate-200 bg-white shadow-sm p-1" data-testid="pricing-cycle-group">
            {["monthly", "annual"].map(c => (
              <button
                key={c}
                onClick={() => setCycle(c)}
                data-testid={`pricing-cycle-${c}`}
                className={`px-5 py-1.5 text-sm font-semibold rounded-full transition ${cycle === c ? "bg-slate-900 text-white shadow" : "text-slate-700 hover:text-slate-900"}`}
              >
                {c === "monthly" ? "Monthly" : "Annual"}
              </button>
            ))}
          </div>
          {cycle === "annual" && annualSavingsPct ? (
            <div className="text-[11px] font-bold text-teal-700 bg-teal-50 border border-teal-200 px-3 py-1 rounded-full">
              Save up to {annualSavingsPct}% with annual billing
            </div>
          ) : null}
        </div>
      </section>

      {/* Plan cards */}
      <section className="max-w-6xl mx-auto px-6 mb-12">
        {loading ? (
          <div className="text-center text-slate-500 py-16" data-testid="pricing-loading">Loading plans…</div>
        ) : activePlans.length === 0 ? (
          <div className="text-center text-slate-500 py-16" data-testid="pricing-empty">
            No published plans are currently available for this product and billing option.
          </div>
        ) : (
          <div className={`grid grid-cols-1 md:grid-cols-${Math.min(3, activePlans.length)} gap-5`} key={`${bundle}-${cycle}`} data-testid="pricing-plan-group">
            {activePlans.map((p, idx) => (
              <PlanCard
                key={p.pricing_offer_id}
                plan={p}
                country={country}
                fmt={fmt}
                selected={p.pricing_offer_id === selectedOfferId}
                onSelect={() => setSelectedOfferId(p.pricing_offer_id)}
                index={idx}
              />
            ))}
          </div>
        )}
      </section>

      {/* Sticky pricing summary — always reflects the currently selected offer */}
      <PricingSummary
        offer={selectedOffer}
        country={country}
        bundle={bundle}
        cycle={cycle}
        fmt={fmt}
        onContinue={onContinue}
      />

      {/* Trust */}
      <section className="max-w-6xl mx-auto px-6 mt-16 mb-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <TrustCard icon={Lock}        title="Enterprise Security" body="256-bit encryption · Audit logs · RBAC" />
          <TrustCard icon={Globe2}      title="Local Ready"          body="Local currency · Tax itemized" />
          <TrustCard icon={Activity}    title="Always Available"     body="99.9% uptime · Active monitoring" />
          <TrustCard icon={RefreshCcw}  title="Scalable"             body="Upgrade tiers or modules any time" />
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 mb-20">
        <h2 className="text-2xl font-bold text-slate-900 text-center mb-6">Frequently asked</h2>
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-200" data-testid="pricing-faq">
          {FAQ.map((item, i) => {
            const open = faqOpen === i;
            return (
              <button
                key={i}
                onClick={() => setFaqOpen(open ? null : i)}
                className="w-full text-left p-4 hover:bg-slate-50 transition"
                data-testid={`pricing-faq-${i}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-slate-900 text-sm">{item.q}</span>
                  <span className={`text-teal-600 text-xs transition ${open ? "rotate-180" : ""}`}>▾</span>
                </div>
                {open ? <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.a}</p> : null}
              </button>
            );
          })}
        </div>
      </section>

      <PricingFooter />
    </div>
  );
}

/* ───────── Plan card ───────── */
function PlanCard({ plan, country, fmt, selected, onSelect, index }) {
  const taxRate = plan.tax_rate ?? (country?.tax_rate ?? 0);
  const vat = plan.tax_inclusive ? 0 : (plan.recurring_amount || 0) * (taxRate / 100);
  const total = (plan.recurring_amount || 0) + (plan.setup_fee || 0) + vat;
  const hasBadge = !!plan.marketing_badge;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group relative text-left rounded-2xl border bg-white p-6 transition-all duration-200 focus:outline-none ${
        selected
          ? "border-teal-500 shadow-lg ring-2 ring-teal-500/40 -translate-y-0.5"
          : "border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5"
      }`}
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`pricing-plan-${plan.pricing_offer_id}`}
      data-selected={selected ? "true" : "false"}
      aria-pressed={selected}
    >
      {hasBadge ? (
        <span
          className={`absolute -top-3 right-6 text-[9px] uppercase tracking-widest font-bold text-white px-3 py-1 rounded-full shadow ${
            plan.marketing_badge?.tone === "amber" ? "bg-gradient-to-r from-amber-500 to-orange-500"
            : plan.marketing_badge?.tone === "violet" ? "bg-gradient-to-r from-violet-500 to-purple-500"
            : plan.marketing_badge?.tone === "cyan" ? "bg-gradient-to-r from-cyan-500 to-blue-500"
            : plan.marketing_badge?.tone === "rose" ? "bg-gradient-to-r from-rose-500 to-red-500"
            : plan.marketing_badge?.tone === "slate" ? "bg-gradient-to-r from-slate-500 to-slate-700"
            : "bg-gradient-to-r from-teal-500 to-cyan-500"
          }`}
          data-testid={`pricing-badge-${plan.pricing_offer_id}`}
        >
          {plan.marketing_badge?.label || ({
            most_popular: "Most Popular", best_value: "Best Value",
            recommended: "Recommended", new: "New", promo: "Promo",
          }[plan.marketing_badge?.type] || "")}
        </span>
      ) : null}

      <div className="flex items-center gap-2 mb-2">
        <div className={`w-4 h-4 rounded-full ${selected ? "bg-teal-500 ring-4 ring-teal-100" : "border border-slate-300"}`} />
        <div className="text-xl font-semibold text-slate-900">{plan.name}</div>
      </div>
      {plan.description ? <p className="text-xs text-slate-500">{plan.description}</p> : null}

      <div className="mt-5">
        {plan.contact_sales ? (
          <>
            <div className="text-3xl font-bold text-slate-900">Custom</div>
            <div className="text-xs text-slate-500 mt-1">Contact sales for pricing</div>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-slate-900">{fmt(plan.recurring_amount)}</span>
              <span className="text-xs text-slate-500">/ {plan.billing_cycle === "annual" ? "year" : "month"}</span>
            </div>
            {(plan.setup_fee || 0) > 0 ? (
              <div className="text-xs text-slate-500 mt-1">+ {fmt(plan.setup_fee)} setup (one-time)</div>
            ) : null}
            <div className="text-[11px] text-slate-400 mt-0.5">
              {plan.tax_inclusive
                ? `${country?.tax_name || "Tax"} included`
                : `${country?.tax_name || "Tax"} ${taxRate}% extra · ${fmt(total)} total today`}
            </div>
          </>
        )}
      </div>

      <PlanLimitsList plan={plan} />

      <div className="mt-6">
        <span
          className={`inline-flex items-center justify-center w-full gap-2 rounded-lg text-sm font-semibold py-2.5 transition ${
            selected ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-900 group-hover:bg-slate-200"
          }`}
          data-testid={`pricing-cta-${plan.pricing_offer_id}`}
        >
          {selected ? "Selected" : (plan.contact_sales ? "Contact Sales" : "Choose Plan")}
          {selected ? <CheckCircle2 className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
        </span>
      </div>
    </button>
  );
}

/**
 * Public plan limits list.
 * Rendered order: (bundle-native scope rows) → (shared rows).
 * A row is included ONLY when the backend has already deemed it
 * customer-visible + in-scope + non-zero-when-disallowed. We still
 * apply the same visibility guard here as a defense in depth.
 */
function PlanLimitsList({ plan }) {
  const rows = pickVisibleLimitRows(plan).slice(0, 5);
  return (
    <ul className="mt-5 space-y-2 text-[12px] text-slate-700" data-testid={`pricing-plan-limits-${plan.pricing_offer_id}`}>
      {rows.map((li, i) => (
        <li key={i} className="flex items-start gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-teal-500 mt-0.5 shrink-0" />
          <span>{li}</span>
        </li>
      ))}
      <li className="flex items-start gap-2">
        <CheckCircle2 className="w-3.5 h-3.5 text-teal-500 mt-0.5 shrink-0" />
        <span>{plan.included_features?.length || 0} features enabled</span>
      </li>
    </ul>
  );
}

function pickVisibleLimitRows(plan) {
  const bundle = plan.module_bundle;
  const grouped = plan.grouped_limits || { certification: [], hr: [], shared: [] };
  const bundleScopes = bundle === "certification" ? ["certification"]
                       : bundle === "hr" ? ["hr"]
                       : ["certification", "hr"];
  const rows = [];
  const format = (l) => {
    // Storage-category rows read as "10 GB Storage" — value + unit + label.
    if (l.category === "storage" && l.unit) return `${l.value} ${l.unit} ${l.display_name}`;
    // Everything else: value + canonical (capitalized) display_name, e.g.
    // "3 Internal Users", "50 Employees".
    return `${l.value} ${l.display_name}`;
  };
  const push = (l) => {
    if (!l) return;
    if (l.customer_visible === false) return;
    if (l.unlimited) { rows.push(`Unlimited ${l.display_name}`); return; }
    if (l.value == null) return;
    if (l.value === 0 && l.allow_zero === false) return;
    rows.push(format(l));
  };
  for (const scope of bundleScopes) (grouped[scope] || []).forEach(push);
  (grouped.shared || []).forEach(push);
  return rows;
}

/* ───────── Sticky pricing summary — reflects the SELECTED offer ───────── */
function PricingSummary({ offer, country, bundle, cycle, fmt, onContinue }) {
  if (!offer || !country) return null;
  const taxRate = offer.tax_rate ?? country.tax_rate ?? 0;
  const vat = offer.tax_inclusive ? 0 : (offer.recurring_amount || 0) * (taxRate / 100);
  const total = (offer.recurring_amount || 0) + (offer.setup_fee || 0) + vat;
  const bundleLabel = BUNDLES.find(b => b.key === bundle)?.label || "";
  const cycleLabel  = cycle === "annual" ? "Annual" : "Monthly";
  return (
    <section className="max-w-3xl mx-auto px-6 -mt-2">
      <div
        className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 shadow-xl"
        data-testid="pricing-sticky-summary"
        data-selected-offer-id={offer.pricing_offer_id}
        data-selected-plan-version-id={offer.plan_version_id}
      >
        <div className="text-[10px] uppercase tracking-[0.2em] text-teal-300 font-bold mb-1" data-testid="pricing-summary-title">
          {offer.name} · {bundleLabel} · {cycleLabel}
        </div>
        <div className="grid grid-cols-2 gap-y-1.5 text-sm">
          <div className="text-slate-300">Subscription</div>
          <div className="text-right font-mono" data-testid="pricing-summary-subscription">
            {offer.contact_sales ? "Custom" : fmt(offer.recurring_amount)}
          </div>
          {(offer.setup_fee || 0) > 0 && !offer.contact_sales ? (
            <>
              <div className="text-slate-300">Setup (one-time)</div>
              <div className="text-right font-mono" data-testid="pricing-summary-setup">{fmt(offer.setup_fee)}</div>
            </>
          ) : null}
          <div className="text-slate-300">{country.tax_name} ({taxRate}%)</div>
          <div className="text-right font-mono" data-testid="pricing-summary-vat">
            {offer.contact_sales ? "—" : fmt(vat)}
          </div>
          <div className="col-span-2 border-t border-white/10 my-1" />
          <div className="font-bold">Total today</div>
          <div className="text-right font-bold font-mono text-teal-300" data-testid="pricing-summary-total">
            {offer.contact_sales ? "Custom" : fmt(total)}
          </div>
        </div>
        <button
          type="button"
          onClick={onContinue}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-teal-500 hover:bg-teal-400 text-slate-900 text-sm font-semibold py-2.5 transition"
          data-testid="pricing-summary-continue"
        >
          {offer.contact_sales ? "Contact Sales" : "Continue to registration"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

function TrustCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition">
      <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-700 border border-teal-100 flex items-center justify-center mb-3">
        <Icon className="w-4 h-4" />
      </div>
      <div className="font-semibold text-slate-900 text-sm">{title}</div>
      <p className="text-xs text-slate-600 mt-1">{body}</p>
    </div>
  );
}

function PricingHeader() {
  // Public marketing surface consumes the platform `website` asset with
  // the built-in wordmark as the packaged fallback. Tenant branding
  // must NOT resolve on public routes (2026-08-06 branding boundary).
  const branding = usePlatformBranding();
  const websiteLogo = brandUrl(branding?.website);
  return (
    <header className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2 font-bold text-slate-900 tracking-tight"
             data-testid="pricing-brand">
        {websiteLogo ? (
          <img src={websiteLogo}
                alt="CertOpz"
                className="max-h-8 w-auto object-contain"
                style={{ maxWidth: 180 }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="pricing-website-logo" />
        ) : (
          "CertOpz"
        )}
      </Link>
      <nav className="flex items-center gap-4 text-sm">
        <Link to="/login" className="text-slate-700 hover:text-slate-900">Sign in</Link>
        <Link to="/register" className="px-3 py-1.5 rounded-md bg-slate-900 text-white font-semibold hover:bg-slate-800">Get started</Link>
      </nav>
    </header>
  );
}

function PricingFooter() {
  // Platform-owned public surface — must display CertOpz identity only.
  // Never resolve platform ownership from a tenant company profile.
  return (
    <footer className="border-t border-slate-200 bg-white py-8 px-6 text-center text-xs text-slate-500">
      © {new Date().getFullYear()} CertOpz ERP. All rights reserved.
    </footer>
  );
}
